const DECIMAL_STRING_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const integerFormatters: Readonly<Record<string, Intl.NumberFormat | undefined>> = {
  hu: new Intl.NumberFormat("hu-HU"),
  ro: new Intl.NumberFormat("ro-RO"),
  it: new Intl.NumberFormat("it-IT"),
  fr: new Intl.NumberFormat("fr-FR"),
  sv: new Intl.NumberFormat("sv-SE"),
  nl: new Intl.NumberFormat("nl-NL"),
  el: new Intl.NumberFormat("el-GR"),
  ru: new Intl.NumberFormat("ru-RU"),
  zh: new Intl.NumberFormat("zh-CN"),
  hi: new Intl.NumberFormat("hi-IN"),
  id: new Intl.NumberFormat("id-ID"),
  ja: new Intl.NumberFormat("ja-JP"),
  bg: new Intl.NumberFormat("bg-BG"),
  pt: new Intl.NumberFormat("pt-PT"),
  "pt-pt": new Intl.NumberFormat("pt-PT"),
  "pt-br": new Intl.NumberFormat("pt-BR"),
  uk: new Intl.NumberFormat("uk-UA"),
  da: new Intl.NumberFormat("da-DK"),
  nb: new Intl.NumberFormat("nb-NO"),
  hr: new Intl.NumberFormat("hr-HR"),
  fi: new Intl.NumberFormat("fi-FI"),
  tr: new Intl.NumberFormat("tr-TR"),
  no: new Intl.NumberFormat("nb-NO"),
};

type DecimalParts = Readonly<{
  fraction: string;
  integer: string;
  negative: boolean;
}>;

export type DecimalFormatOptions = Readonly<{
  locale?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}>;

function parts(value: string): DecimalParts {
  if (!DECIMAL_STRING_PATTERN.test(value)) {
    throw new TypeError("Invalid decimal string.");
  }

  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const [integer, fraction = ""] = unsigned.split(".");

  return { fraction, integer, negative };
}

function scaledInteger(value: string, scale: number): bigint {
  const parsed = parts(value);
  const normalizedFraction = parsed.fraction.slice(0, scale).padEnd(scale, "0");
  const magnitude = BigInt(`${parsed.integer}${normalizedFraction}`);

  return parsed.negative ? -magnitude : magnitude;
}

function fromScaledInteger(value: bigint, scale: number, minimumFractionDigits = 0): string {
  const negative = value < BigInt(0);
  const digits = (negative ? -value : value).toString().padStart(scale + 1, "0");
  const integer = scale > 0 ? digits.slice(0, -scale) : digits;
  const fullFraction = scale > 0 ? digits.slice(-scale) : "";
  const fraction = fullFraction.replace(/0+$/, "").padEnd(minimumFractionDigits, "0");
  const sign = negative && (integer !== "0" || /[1-9]/.test(fraction)) ? "-" : "";

  return `${sign}${integer}${fraction ? `.${fraction}` : ""}`;
}

function maximumScale(values: readonly string[]): number {
  return values.reduce((current, value) => Math.max(current, parts(value).fraction.length), 0);
}

export function isDecimalString(value: unknown, maximumFractionDigits = 6): value is string {
  if (typeof value !== "string" || !DECIMAL_STRING_PATTERN.test(value)) {
    return false;
  }

  return parts(value).fraction.length <= maximumFractionDigits;
}

/** Normalizes legacy integer JSON quantities while preserving decimal strings verbatim. */
export function decimalStringFromApiValue(value: string | number): string {
  if (typeof value === "string") {
    parts(value);

    return value;
  }

  if (!Number.isSafeInteger(value)) {
    throw new TypeError("Legacy numeric quantity must be a safe integer.");
  }

  return String(value);
}

export function normalizeDecimalString(value: string, scale?: number): string {
  const parsed = parts(value);
  const targetScale = scale ?? parsed.fraction.length;

  if (!Number.isInteger(targetScale) || targetScale < 0) {
    throw new RangeError("Decimal scale must be a non-negative integer.");
  }

  if (parsed.fraction.length > targetScale && /[1-9]/.test(parsed.fraction.slice(targetScale))) {
    throw new RangeError("Decimal value exceeds the requested scale.");
  }

  return fromScaledInteger(scaledInteger(value, targetScale), targetScale, targetScale);
}

export function sumDecimalStrings(values: readonly string[]): string {
  if (values.length === 0) return "0";
  const scale = maximumScale(values);
  const total = values.reduce((current, value) => current + scaledInteger(value, scale), BigInt(0));

  return fromScaledInteger(total, scale);
}

export function addDecimalStrings(left: string, right: string): string {
  return sumDecimalStrings([left, right]);
}

export function subtractDecimalStrings(left: string, right: string): string {
  const scale = maximumScale([left, right]);

  return fromScaledInteger(
    scaledInteger(left, scale) - scaledInteger(right, scale),
    scale,
  );
}

export function compareDecimalStrings(left: string, right: string): -1 | 0 | 1 {
  const scale = maximumScale([left, right]);
  const leftValue = scaledInteger(left, scale);
  const rightValue = scaledInteger(right, scale);

  return leftValue === rightValue ? 0 : leftValue < rightValue ? -1 : 1;
}

export function absoluteDecimalString(value: string): string {
  const parsed = parts(value);

  return fromScaledInteger(scaledInteger(value, parsed.fraction.length) < BigInt(0)
    ? -scaledInteger(value, parsed.fraction.length)
    : scaledInteger(value, parsed.fraction.length), parsed.fraction.length);
}

/**
 * Adapts an exact decimal string to an old JSON integer contract.
 *
 * This must stay at a legacy API boundary: fractional and unsafe integer
 * values are rejected instead of being rounded or losing precision.
 */
export function legacyIntegerFromDecimalString(value: string): number {
  const parsed = parts(value);

  if (/[1-9]/.test(parsed.fraction)) {
    throw new RangeError("Legacy integer quantity cannot contain a fraction.");
  }

  const integer = BigInt(`${parsed.negative ? "-" : ""}${parsed.integer}`);
  if (integer < BigInt("-9007199254740991") || integer > BigInt("9007199254740991")) {
    throw new RangeError("Legacy integer quantity exceeds the safe JSON integer range.");
  }

  return parseInt(integer.toString(), 10);
}

export function multiplyDecimalStringByPowerOfTen(value: string, places: number): string {
  if (!Number.isInteger(places) || places < 0) {
    throw new RangeError("Decimal shift must be a non-negative integer.");
  }

  const parsed = parts(value);
  const nextScale = Math.max(0, parsed.fraction.length - places);
  const magnitude = scaledInteger(value, parsed.fraction.length) * (BigInt(10) ** BigInt(Math.max(0, places - parsed.fraction.length)));

  return fromScaledInteger(magnitude, nextScale);
}

export function decimalPercentageOf(
  value: string,
  maximum: string,
  fractionDigits = 4,
): string {
  if (!Number.isInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > 8) {
    throw new RangeError("Percentage scale must be between zero and eight.");
  }

  const scale = maximumScale([value, maximum]);
  const numerator = scaledInteger(value, scale);
  const denominator = scaledInteger(maximum, scale);
  if (denominator <= BigInt(0)) return "0";

  const factor = BigInt(100) * (BigInt(10) ** BigInt(fractionDigits));
  const absoluteNumerator = numerator < BigInt(0) ? -numerator : numerator;
  const quotient = absoluteNumerator * factor / denominator;
  const remainder = absoluteNumerator * factor % denominator;
  const rounded = remainder * BigInt(2) >= denominator ? quotient + BigInt(1) : quotient;

  return fromScaledInteger(rounded, fractionDigits);
}

export function formatDecimalString(
  value: string,
  options: DecimalFormatOptions = {},
): string {
  const parsed = parts(value);
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  const minimumFractionDigits = options.minimumFractionDigits ?? 0;

  if (!Number.isInteger(maximumFractionDigits) || maximumFractionDigits < 0) {
    throw new RangeError("Maximum fraction digits must be a non-negative integer.");
  }

  if (!Number.isInteger(minimumFractionDigits) || minimumFractionDigits < 0 || minimumFractionDigits > maximumFractionDigits) {
    throw new RangeError("Minimum fraction digits must fit the configured maximum.");
  }

  let rounded: bigint;
  if (parsed.fraction.length <= maximumFractionDigits) {
    rounded = scaledInteger(value, maximumFractionDigits);
  } else {
    const original = scaledInteger(value, parsed.fraction.length);
    const negative = original < BigInt(0);
    const magnitude = negative ? -original : original;
    const divisor = BigInt(10) ** BigInt(parsed.fraction.length - maximumFractionDigits);
    const quotient = magnitude / divisor;
    const remainder = magnitude % divisor;
    const roundedMagnitude = remainder * BigInt(2) >= divisor ? quotient + BigInt(1) : quotient;
    rounded = negative ? -roundedMagnitude : roundedMagnitude;
  }

  const normalized = fromScaledInteger(rounded, maximumFractionDigits, minimumFractionDigits);
  const negative = normalized.startsWith("-");
  const unsigned = negative ? normalized.slice(1) : normalized;
  const [integer, fraction] = unsigned.split(".");
  const locale = (options.locale ?? "en").toLowerCase().replaceAll("_", "-");
  const czech = locale.startsWith("cs");
  const german = locale.startsWith("de");
  const polish = locale.startsWith("pl");
  const slovak = locale.startsWith("sk");
  const spanish = locale.startsWith("es");
  const hungarian = locale.startsWith("hu");
  const romanian = locale.startsWith("ro");
  const italian = locale.startsWith("it");
  const french = locale.startsWith("fr");
  const swedish = locale.startsWith("sv");
  const dutch = locale.startsWith("nl");
  const greek = locale.startsWith("el");
  const russian = locale.startsWith("ru");
  const indonesian = locale.startsWith("id");
  const bulgarian = locale.startsWith("bg");
  const portuguese = locale.startsWith("pt");
  const ukrainian = locale.startsWith("uk");
  const danish = locale.startsWith("da");
  const norwegian = locale.startsWith("nb") || locale.startsWith("no");
  const croatian = locale.startsWith("hr");
  const finnish = locale.startsWith("fi");
  const turkish = locale.startsWith("tr");
  const integerFormatter = integerFormatters[locale] ?? integerFormatters[locale.slice(0, 2)];
  const groupedInteger = integerFormatter
    ? integerFormatter.format(BigInt(integer))
    : spanish && integer.length < 5
      ? integer
      : integer.replace(/\B(?=(\d{3})+(?!\d))/g, czech || polish || slovak ? "\u00a0" : german || spanish ? "." : ",");

  return `${negative ? "-" : ""}${groupedInteger}${fraction === undefined ? "" : `${czech || german || polish || slovak || spanish || hungarian || romanian || italian || french || swedish || dutch || greek || russian || indonesian || bulgarian || portuguese || ukrainian || danish || norwegian || croatian || finnish || turkish ? "," : "."}${fraction}`}`;
}
